import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  Loader2,
  LockKeyhole,
  Mail,
  Users,
} from "lucide-react";

import {
  inspectActivationInvite,
  onboardingErrorMessage,
  redeemActivationInvite,
} from "../api/onboarding";


const LIMITS = [
  { icon: CalendarDays, value: "30 days", label: "Trial access" },
  { icon: Users, value: "Up to 5", label: "Team members" },
  { icon: Building2, value: "3 live", label: "Quality incidents" },
];


export default function ActivationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function loadInvite() {
      if (!token) {
        setError("This activation link is incomplete. Ask your Fluxonix contact for a new invitation.");
        setLoading(false);
        return;
      }
      try {
        const data = await inspectActivationInvite(token);
        if (active) setInvite(data);
      } catch (err) {
        if (active) setError(onboardingErrorMessage(err, "We could not verify this invitation."));
      } finally {
        if (active) setLoading(false);
      }
    }
    loadInvite();
    return () => { active = false; };
  }, [token]);

  async function activate() {
    if (!invite || !fullName.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await redeemActivationInvite({
        token,
        email: invite.email,
        full_name: fullName.trim(),
      });
      sessionStorage.setItem("inferred_slug", result.tenant.slug);
      navigate("/otp-login", {
        replace: true,
        state: {
          email: result.user.email,
          returnTo: "/onboarding/welcome",
          onboarding: true,
        },
      });
    } catch (err) {
      setError(onboardingErrorMessage(err, "Activation failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  const unavailable = invite && !["pending", "redeemed"].includes(invite.status);

  return (
    <div className="min-h-[calc(100vh-8rem)] py-8 lg:py-14">
      <div className="mx-auto grid max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-950/5 lg:grid-cols-[0.88fr_1.12fr]">
        <aside className="relative overflow-hidden bg-[#10214a] px-7 py-7 text-white sm:px-10 sm:py-9 lg:py-12">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="relative">
            <div className="mb-7 flex items-center gap-3 sm:mb-12">
              <div className="rounded-xl bg-white/10 p-2.5"><LockKeyhole size={24} /></div>
              <div><div className="font-bold tracking-wide">FLUXONIX</div><div className="text-xs text-blue-200">Quality operations, accelerated</div></div>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200">Invite-only access</p>
            <h1 className="mt-3 text-2xl font-bold leading-tight sm:text-3xl">Turn frontline evidence into a review-ready NCR.</h1>
            <p className="mt-4 hidden text-sm leading-6 text-blue-100/80 sm:block">Capture voice, photos, notes, and corrective actions in one guided quality workflow.</p>
            <div className="mt-9 hidden space-y-4 sm:block">
              {["Guided sample event included", "No integrations required", "No Microsoft admin consent"].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm text-blue-50"><span className="rounded-full bg-emerald-400/20 p-1 text-emerald-300"><Check size={14} /></span>{item}</div>
              ))}
            </div>
          </div>
        </aside>

        <main className="px-6 py-8 sm:px-10 lg:px-12 lg:py-12">
          {loading ? (
            <div className="flex min-h-96 items-center justify-center text-slate-500"><Loader2 className="mr-3 animate-spin" /> Verifying your invitation…</div>
          ) : error && !invite ? (
            <StateMessage icon={AlertCircle} title="Invitation unavailable" message={error} />
          ) : unavailable ? (
            <StateMessage icon={AlertCircle} title={`Invitation ${invite.status}`} message="Ask your Fluxonix contact to issue a fresh activation link." />
          ) : (
            <>
              <p className="text-sm font-semibold text-blue-700">Welcome to Fluxonix</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">Activate your workspace</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">Confirm your details. We’ll provision your private workspace and then verify your email.</p>

              <div className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start gap-3"><Building2 className="mt-0.5 text-blue-700" size={20} /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-400">Organization</div><div className="mt-1 font-semibold text-slate-900">{invite.organization_name}</div></div></div>
                <div className="mt-4 flex items-start gap-3"><Mail className="mt-0.5 text-blue-700" size={20} /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-400">Invited administrator</div><div className="mt-1 font-semibold text-slate-900">{invite.email}</div></div></div>
              </div>

              <label className="mt-6 block text-sm font-semibold text-slate-700" htmlFor="full-name">Your full name</label>
              <input id="full-name" autoFocus value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder="e.g. Alex Morgan" className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-blue-600 focus:ring-4 focus:ring-blue-100" />

              <div className="mt-6 grid grid-cols-3 gap-2">
                {LIMITS.map(({ icon: Icon, value, label }) => <div key={label} className="rounded-xl border border-slate-200 p-3 text-center"><Icon className="mx-auto text-blue-700" size={18} /><div className="mt-2 text-sm font-bold text-slate-900">{value}</div><div className="mt-0.5 text-[11px] text-slate-500">{label}</div></div>)}
              </div>

              {error && <div className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
              <button onClick={activate} disabled={!fullName.trim() || submitting} className="mt-7 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-700 font-bold text-white shadow-lg shadow-blue-700/20 transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">
                {submitting ? <><Loader2 className="animate-spin" size={19} /> Creating workspace…</> : <>Activate workspace <ArrowRight size={19} /></>}
              </button>
              <p className="mt-4 text-center text-xs leading-5 text-slate-400">By continuing, you confirm that you are authorized to create this workspace for {invite.organization_name}.</p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}


function StateMessage({ icon: Icon, title, message }) {
  return <div className="flex min-h-96 flex-col items-center justify-center text-center"><div className="rounded-2xl bg-amber-50 p-4 text-amber-700"><Icon size={32} /></div><h2 className="mt-5 text-2xl font-bold text-slate-900">{title}</h2><p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">{message}</p></div>;
}
